// frontend/src/pages/admin/PlayerAchievements.jsx
import React, { useCallback, useEffect, useState } from "react";
import API from "../../api/api";
import Navbar from "../../components/Navbar";
import Swal from "sweetalert2";

const PlayerAchievements = () => {
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState(null);

  const [sports, setSports] = useState([]);
  const [games, setGames] = useState([]);
  const [playerClassifications, setPlayerClassifications] = useState([]);

  const [form, setForm] = useState({
    id: null,
    game_id: "",
    sport_ui: "", // sport id for UI
    event_name: "",
    event_location: "",
    event_level: "District",
    medal: "None",
    event_date: "",
    notes: "",
  });

  // --------- Players ----------
  const loadPlayers = async () => {
    try {
      setLoading(true);
      // Call new full-list endpoint
      let res;
      try {
        res = await API.fetchAllPlayersFull();
      } catch (e) {
        // Fallback to paginated endpoint when /all-players is unavailable
        res = await API.fetchAdminPlayers();
      }
      // res should already be an array thanks to interceptor, but be defensive:
      const playersData = Array.isArray(res)
        ? res
        : res?.data?.data || res?.data || res?.players || [];

      setPlayers(playersData);

      if (playersData.length === 0) {
        Swal.fire({
          icon: "info",
          title: "No Players",
          text: "Add players first in Players Management",
        });
      }
    } catch (err) {
      console.error("Players load error:", err);
      Swal.fire("Failed to load players");
    } finally {
      setLoading(false);
    }
  };

  // --------- Sports & Games ----------
  const loadSports = async () => {
    try {
      const res = await API.getSports();
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setSports(list);
    } catch (err) {
      console.error("Sports load error:", err);
      setSports([]);
    }
  };

  const loadGames = async (sportId) => {
    try {
      if (!sportId) {
        setGames([]);
        return;
      }
      const res = await API.fetchGamesBySport(sportId);
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setGames(list);
    } catch (err) {
      console.error("Games load error:", err);
      setGames([]);
    }
  };

  // --------- Achievements ----------
  const loadAchievements = useCallback(async () => {
    if (!selectedPlayer) {
      setAchievements([]);
      return;
    }

    try {
      const res = await API.fetchPlayerAchievements(selectedPlayer);
      const achievementsData = Array.isArray(res) ? res : [];
      setAchievements(achievementsData);
    } catch (err) {
      console.error(
        "Achievements error:",
        err?.status,
        err?.message || err
      );
      Swal.fire({
        icon: "error",
        title: "Failed to load achievements",
        text: err?.message || "Unknown error",
      });
      setAchievements([]);
    }
  }, [selectedPlayer]);

  const loadPlayerClassifications = async (playerId) => {
    if (!playerId) {
      setPlayerClassifications([]);
      return;
    }
    try {
      const res = await API.fetchClassifiedPlayers({ player_id: playerId });
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];
      setPlayerClassifications(list);
    } catch (err) {
      console.error("Classification load error:", err);
      setPlayerClassifications([]);
    }
  };

  const saveAchievement = async () => {
    if (!form.event_name.trim()) {
      Swal.fire("Event name required");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await API.updatePlayerAchievement(editingId, {
          ...form,
          game_id: form.game_id || null,
        });
        Swal.fire("✅ Updated successfully!", "", "success");
      } else {
        await API.addPlayerAchievement({
          ...form,
          player_id: selectedPlayer,
          game_id: form.game_id || null,
        });
        Swal.fire("✅ Added successfully!", "", "success");
      }

      setForm({
        id: null,
        game_id: "",
        sport_ui: "",
        event_name: "",
        event_location: "",
        event_level: "District",
        medal: "None",
        event_date: "",
        notes: "",
      });
      setEditingId(null);
      loadAchievements();
    } catch (err) {
      console.error("Save error:", err);
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: err?.message || "Try again",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteAchievement = async (id) => {
    const result = await Swal.fire({
      title: "Delete achievement?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
    });

    if (result.isConfirmed) {
      try {
        await API.deletePlayerAchievement(id);
        Swal.fire("✅ Deleted!", "", "success");
        loadAchievements();
      } catch (err) {
        console.error("Delete error:", err);
        Swal.fire("❌ Delete failed", "", "error");
      }
    }
  };

  const editAchievement = (achievement) => {
    setEditingId(achievement.id);
    setForm({
      id: achievement.id,
      game_id: achievement.game_id || "",
      sport_ui: "", // will be set if user chooses sport filter
      event_name: achievement.event_name || "",
      event_location: achievement.event_location || "",
      event_level: achievement.event_level || "District",
      medal: achievement.medal || "None",
      event_date: achievement.event_date || "",
      notes: achievement.notes || "",
    });

    // best effort: find sport by name and load its games
    const sportFromGame = sports.find(
      (s) => s.name === achievement.sport_name
    );
    if (sportFromGame) {
      setForm((prev) => ({ ...prev, sport_ui: String(sportFromGame.id) }));
      loadGames(sportFromGame.id);
    }
  };

  // --------- Effects ----------
  useEffect(() => {
    loadPlayers();
    loadSports();
  }, []);

  useEffect(() => {
    loadAchievements();
  }, [loadAchievements]);

  useEffect(() => {
    loadPlayerClassifications(selectedPlayer);
  }, [selectedPlayer]);

  useEffect(() => {
    if (form.sport_ui) {
      loadGames(form.sport_ui);
    } else {
      setGames([]);
    }
  }, [form.sport_ui]);

  const selectedPlayerName =
    players.find((p) => String(p.id) === String(selectedPlayer))?.name ||
    "";

  const selectedGame = games.find((g) => String(g.id) === String(form.game_id));
  const selectedSportId = form.sport_ui || selectedGame?.sport_id || "";
  const selectedPlayerClassification = playerClassifications.find(
    (c) => String(c.sport_id) === String(selectedSportId)
  );

  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto p-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-4">
            🏅 Player Achievements
          </h1>
          <p className="text-xl text-gray-600">
            Medals • Awards • Competition History
          </p>
        </div>

        {/* Player Selector */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 mb-8 border">
          <label className="block text-xl font-bold text-gray-800 mb-4">
            Select Player ({players.length})
          </label>

          <select
            value={selectedPlayer}
            onChange={(e) => setSelectedPlayer(e.target.value)}
            disabled={loading}
            className="w-full p-4 text-lg border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-emerald-500 focus:border-emerald-500 shadow-lg"
          >
            <option value="">📋 Choose player from list...</option>
            {players.map((player) => (
              <option key={player.id} value={player.id}>
                {player.name}{" "}
                {player.district ? `• ${player.district}` : ""}{" "}
                {player.aadhaar_number
                  ? `• ${player.aadhaar_number.slice(-4)}`
                  : ""}
              </option>
            ))}
          </select>

          {loading && (
            <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl text-center">
              <div className="text-blue-800 font-semibold">
                🔄 Loading players...
              </div>
            </div>
          )}
        </div>

        {/* Form */}
        {selectedPlayer && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-3xl p-8 mb-8 shadow-2xl">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {editingId ? "✏️ Edit" : "➕ Add"} Achievement for{" "}
              {selectedPlayerName}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Sport (UI only, optional) */}
              <div>
                <label className="block font-semibold mb-2">🏅 Sport</label>
                <select
                className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-purple-500"
                value={form.sport_ui}
                onChange={(e) =>
                  setForm({
                    ...form,
                    sport_ui: e.target.value,
                    game_id: "",
                  })
                }
              >
                <option value="">All sports / not specific</option>
                {sports.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              </div>

              {/* Game (stored as game_id) */}
              <div>
                <label className="block font-semibold mb-2">
                  🎮 Game / Event
                </label>
                <select
                  className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-purple-500"
                  value={form.game_id}
                  onChange={(e) =>
                    setForm({ ...form, game_id: e.target.value })
                  }
                  disabled={games.length === 0}
                >
                  <option value="">
                    {games.length ? "Select game..." : "No games for sport"}
                  </option>
                  {games.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.game_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-semibold mb-2">♿ Classification</label>
                <div className="w-full p-4 border rounded-xl bg-gray-50 min-h-[58px] flex items-center">
                  {selectedPlayerClassification?.classification ? (
                    <span className="font-semibold text-emerald-700">
                      {selectedPlayerClassification.classification}
                    </span>
                  ) : (
                    <span className="text-gray-500">
                      {selectedSportId ? "No classification assigned for selected sport" : "Select sport or game"}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="block font-semibold mb-2">
                  🏆 Event Name *
                </label>
                <input
                  type="text"
                  placeholder="National Para Athletics 2024"
                  className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-blue-500"
                  value={form.event_name}
                  onChange={(e) =>
                    setForm({ ...form, event_name: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block font-semibold mb-2">📍 Location</label>
                <input
                  type="text"
                  placeholder="Delhi, India"
                  className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-blue-500"
                  value={form.event_location}
                  onChange={(e) =>
                    setForm({ ...form, event_location: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block font-semibold mb-2">🥇 Medal</label>
                <select
                  className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-amber-500"
                  value={form.medal}
                  onChange={(e) =>
                    setForm({ ...form, medal: e.target.value })
                  }
                >
                  <option value="Gold">🥇 Gold</option>
                  <option value="Silver">🥈 Silver</option>
                  <option value="Bronze">🥉 Bronze</option>
                  <option value="None">📍 Participated</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold mb-2">📅 Date</label>
                <input
                  type="date"
                  className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-green-500"
                  value={form.event_date}
                  onChange={(e) =>
                    setForm({ ...form, event_date: e.target.value })
                  }
                />
              </div>

              <div className="lg:col-span-2">
                <label className="block font-semibold mb-2">🏛️ Level</label>
                <select
                  className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-purple-500"
                  value={form.event_level}
                  onChange={(e) =>
                    setForm({ ...form, event_level: e.target.value })
                  }
                >
                  <option value="District">🏛️ District</option>
                  <option value="State">🏛️ State</option>
                  <option value="National">🇮🇳 National</option>
                  <option value="International">🌍 International</option>
                </select>
              </div>

              <div className="lg:col-span-3">
                <label className="block font-semibold mb-2">📝 Notes</label>
                <textarea
                  rows="3"
                  placeholder="Additional details, rankings, records..."
                  className="w-full p-4 border rounded-xl focus:ring-4 focus:ring-indigo-500 resize-vertical"
                  value={form.notes}
                  onChange={(e) =>
                    setForm({ ...form, notes: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <button
                onClick={saveAchievement}
                disabled={saving || !form.event_name.trim()}
                className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-4 px-8 rounded-2xl font-bold text-lg hover:from-emerald-700 hover:to-teal-700 shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-1"
              >
                {saving
                  ? "💾 Saving..."
                  : editingId
                  ? "✏️ Update Achievement"
                  : "➕ Add Achievement"}
              </button>

              {editingId && (
                <button
                  onClick={() => {
                    setForm({
                      id: null,
                      game_id: "",
                      sport_ui: "",
                      event_name: "",
                      event_location: "",
                      event_level: "District",
                      medal: "None",
                      event_date: "",
                      notes: "",
                    });
                    setEditingId(null);
                  }}
                  className="px-8 py-4 bg-gray-500 text-white rounded-2xl font-semibold hover:bg-gray-600 transition-all"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}

        {/* Achievements Table */}
        {achievements.length > 0 && (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                🏆 Achievements ({achievements.length})
                <span className="text-emerald-200 text-sm font-normal">
                  for {selectedPlayerName}
                </span>
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Sport / Game
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Classification
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Event
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Level
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Medal
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Notes
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {achievements.map((achievement) => (
                    <tr
                      key={achievement.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="font-semibold">
                          {achievement.sport_name || "—"}
                        </div>
                        <div className="text-gray-600 text-xs">
                          {achievement.game_name || "—"}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {achievement.classification_name || "—"}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-semibold text-gray-900">
                          {achievement.event_name}
                        </div>
                        {achievement.event_location && (
                          <div className="text-sm text-gray-500">
                            {achievement.event_location}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                          {achievement.event_level}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-bold ${
                            achievement.medal === "Gold"
                              ? "bg-amber-100 text-amber-800"
                              : achievement.medal === "Silver"
                              ? "bg-gray-100 text-gray-800"
                              : achievement.medal === "Bronze"
                              ? "bg-orange-100 text-orange-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {achievement.medal === "None"
                            ? "📍 Participated"
                            : achievement.medal}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {achievement.event_date
                          ? new Date(
                              achievement.event_date
                            ).toLocaleDateString()
                          : "TBD"}
                      </td>
                      <td className="px-6 py-4">
                        {achievement.notes ? (
                          <div
                            className="text-sm text-gray-600 max-w-xs truncate"
                            title={achievement.notes}
                          >
                            {achievement.notes}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">
                            No notes
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={() => editAchievement(achievement)}
                            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-semibold rounded-xl hover:from-blue-600 hover:to-blue-700 shadow-md transition-all transform hover:-translate-y-0.5"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() =>
                              deleteAchievement(achievement.id)
                            }
                            className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white text-sm font-semibold rounded-xl hover:from-red-600 hover:to-red-700 shadow-md transition-all transform hover:-translate-y-0.5"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {selectedPlayer && achievements.length === 0 && !loading && (
          <div className="text-center py-20 bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl border-2 border-dashed border-gray-300">
            <div className="text-6xl mb-6">🏆</div>
            <h3 className="text-2xl font-bold text-gray-700 mb-4">
              No Achievements Yet
            </h3>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              Add the first achievement for {selectedPlayerName} using the
              form above.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default PlayerAchievements;
